import type {
  ApolloCache,
  DefaultContext,
  MutationUpdaterFunction,
} from '@apollo/client';
import { useApolloClient } from '@apollo/client';
import { cloneDeep } from '@apollo/client/utilities';
import { useInterval } from '@hover/blueprint/chakra';
import { sortBy, take } from 'ramda';
import { useCallback, useEffect, useState } from 'react';

import { AI_GENERATION_APP_ID } from 'src/modules/homeownerApp/constants';
import {
  useAvailableStyles,
  useHomeownerAppContext,
} from 'src/modules/homeownerApp/hooks';
import {
  ALL_STYLES,
  GeneratedImageStatus,
} from 'src/modules/homeownerApp/static_values';
import type {
  AiGeneratedImageClientStatusFragment,
  AiGeneratedImagePollingConsumerFragment,
  AiOriginalImageQuery,
  AiOriginalImageQueryVariables,
  AiOriginalImagesQuery,
  AiOriginalImagesQueryVariables,
  GenerateAiImageMutation,
  GenerateAiImageMutationVariables,
} from 'src/shared/graphql/generated';
import {
  AiGeneratedImageClientStatusFragmentDoc,
  AiGeneratedImagePollingConsumerFragmentDoc,
  AiOriginalImageDocument,
  AiOriginalImagesDocument,
  useAiGeneratedImageQuery,
  useGenerateAiImageMutation,
} from 'src/shared/graphql/generated';
import { useIsOnline } from 'src/shared/hooks';
import { EventType, emitPageAction, imageLoaded } from 'src/shared/telemetry';
import { logError } from 'src/shared/telemetry/logger';

import { getImageOrgId, isGeneratingImage } from '../helpers';
import type {
  AiOrSampleGeneratedImage,
  AiOrSampleOriginalImage,
} from '../types';
import { getImageId, isSampleGenerated } from '../types';

const DEFAULT_CONFIG: Omit<
  GenerateAiImageMutationVariables,
  'style' | 'aiOriginalImageId'
> = {
  appId: AI_GENERATION_APP_ID,
};

export const POLLING_INTERVAL = 1000;
export const MAX_IMAGE_LOAD_TIME = 30000;
export const MAX_RETRIES = 3;

const updateAiOriginalImagesCache: MutationUpdaterFunction<
  GenerateAiImageMutation,
  GenerateAiImageMutationVariables,
  DefaultContext,
  ApolloCache<any>
> = (cache, mutationResult) => {
  if (!mutationResult.data?.generateAiImage) return;

  const createdImage = mutationResult.data.generateAiImage.aiGeneratedImage;
  const createdDisplayImage = mutationResult.data.generateAiImage;

  cache.updateQuery<AiOriginalImageQuery, AiOriginalImageQueryVariables>(
    {
      query: AiOriginalImageDocument,
      variables: { id: String(createdImage.aiOriginalImageId) },
    },
    prevData => {
      const nextData = cloneDeep(prevData);

      nextData?.aiOriginalImage?.aiDisplayImages?.push(createdDisplayImage);

      return nextData;
    },
  );

  cache.updateQuery<AiOriginalImagesQuery, AiOriginalImagesQueryVariables>(
    { query: AiOriginalImagesDocument, variables: {} },
    prevData => {
      if (!prevData || !prevData.aiOriginalImages) {
        return undefined;
      }

      const existingResultIndex = prevData.aiOriginalImages.findIndex(
        result => result?.id === createdImage.aiOriginalImageId,
      );

      if (existingResultIndex > -1) {
        const nextData = cloneDeep(prevData);

        if (nextData.aiOriginalImages) {
          nextData.aiOriginalImages[
            existingResultIndex
          ]?.aiGeneratedImages?.push(createdImage);
        }

        return nextData;
      }

      return undefined;
    },
  );
};

export const useGenerateImageHandler = (
  variables: Omit<GenerateAiImageMutationVariables, 'style'>,
) => {
  const { aiOriginalImageId } = variables;

  const [generateAiImage, generateAiImageResult] = useGenerateAiImageMutation();

  /**
   * Make the query to start generating a new AI
   * image and update the cache accordingly
   *
   * @param style the style that will be used to generate the created image
   */
  const generateImageHandler = useCallback(
    (style: string) => {
      if (!aiOriginalImageId) {
        return;
      }

      void (async () => {
        const createdResult = await generateAiImage({
          variables: { ...variables, style },
          update: updateAiOriginalImagesCache,
        });

        if (createdResult.errors?.length) {
          createdResult.errors.forEach(e => logError(e));
        }
        if (createdResult.data?.generateAiImage?.aiGeneratedImage.id) {
          emitPageAction(EventType.DESIGNER_GENERATED_IMAGE_NEW, {
            aiOriginalImageId: String(aiOriginalImageId),
            aiGeneratedImageId: String(
              createdResult.data?.generateAiImage?.aiGeneratedImage.id,
            ),
          });
        }
      })();
    },
    [aiOriginalImageId, generateAiImage, variables],
  );

  return [generateImageHandler, generateAiImageResult] as const;
};

/**
 * Get a function that reads the polling consumer ID "lock" from the cache for a
 * generated image
 *
 * @param id generated image ID
 */
const useReadPollingConsumerId = (id: string) => {
  const client = useApolloClient();

  return useCallback(() => {
    const pollingConsumerCache =
      client.readFragment<AiGeneratedImagePollingConsumerFragment>({
        id: `AiGeneratedImage:${id}`,
        fragment: AiGeneratedImagePollingConsumerFragmentDoc,
      });

    return pollingConsumerCache?.pollingConsumerId;
  }, [client, id]);
};

/**
 * Get a function that reads the client status from cache for a
 * generated image
 *
 * @param id generated image ID
 */
const useReadClientStatus = (id: string) => {
  const client = useApolloClient();

  return useCallback(() => {
    const clientStatusCache =
      client.readFragment<AiGeneratedImageClientStatusFragment>({
        id: `AiGeneratedImage:${id}`,
        fragment: AiGeneratedImageClientStatusFragmentDoc,
      });

    return clientStatusCache?.clientStatus;
  }, [client, id]);
};

/**
 * Get a function to write a polling consumer ID "lock" to the cache for a
 * generated image to mark that it's being polled by a particular consumer
 *
 * @param id generated image ID
 */
const useWritePollingConsumerId = (id: string) => {
  const client = useApolloClient();

  return useCallback(
    (consumerId: string | null) =>
      client.writeFragment<AiGeneratedImagePollingConsumerFragment>({
        id: `AiGeneratedImage:${id}`,
        fragment: AiGeneratedImagePollingConsumerFragmentDoc,
        data: { pollingConsumerId: consumerId },
      }),
    [client, id],
  );
};

/**
 * We specifically want to track whether they went offline at while the image
 * was generating, not whether they are currently online
 *
 * @returns boolean indicating whether the user went offline
 */
const useWentOffline = () => {
  const isOnline = useIsOnline();
  const [wentOffline, setWentOffline] = useState(!isOnline);

  if (!wentOffline && !isOnline) setWentOffline(true);

  return wentOffline;
};

/**
 * Used to update the AiGeneratedImage status in when polling time exceeds the
 * maximum allowed amount of time.
 */
export const useWriteClientStatus = (id: string) => {
  const client = useApolloClient();

  return useCallback(
    (clientStatus: GeneratedImageStatus) =>
      client.writeFragment<AiGeneratedImageClientStatusFragment>({
        id: `AiGeneratedImage:${id}`,
        fragment: AiGeneratedImageClientStatusFragmentDoc,
        data: { clientStatus },
      }),
    [client, id],
  );
};

const reportFailedStatus = (
  originalImageId: number,
  generatedImageId: number,
) => {
  emitPageAction(EventType.DESIGNER_GENERATED_IMAGE_FAILED, {
    aiOriginalImageId: `${originalImageId}`,
    aiGeneratedImageId: `${generatedImageId}`,
  });
};

export interface UseGeneratedImagePollingParams {
  consumerId: string;
  image: AiOrSampleGeneratedImage;
  overrideMaxPollingTime?: number;
}

export const useGeneratedImagePolling = ({
  consumerId,
  image,
  overrideMaxPollingTime,
}: UseGeneratedImagePollingParams) => {
  const { trackingData } = useHomeownerAppContext();

  const wentOffline = useWentOffline();
  const [imageLoadStartTime] = useState(Date.now());
  const [numRetries, setNumRetries] = useState(0);

  const id = String(getImageId(image));
  const { spaceType } = image;

  const readConsumerId = useReadPollingConsumerId(id);
  const readClientStatus = useReadClientStatus(id);
  const writeConsumerId = useWritePollingConsumerId(id);
  const updateClientStatus = useWriteClientStatus(id);

  const maxPollingTime = overrideMaxPollingTime || MAX_IMAGE_LOAD_TIME;

  const pollingConsumerId = readConsumerId();
  const clientStatus = readClientStatus();
  const isGenerating = isGeneratingImage(image);

  useEffect(() => {
    if (!isGenerating) return undefined;

    // If the image is currently generating and no polling consumer ID is set
    // for the image in the cache, set it to the provided `consumerId`. This is
    // effectively a "lock" on polling for this generated image.
    if (!readConsumerId()) writeConsumerId(consumerId);

    return () => {
      // Release the polling lock when generation completes
      // or when the polling component is unmounted
      if (readConsumerId() === consumerId) writeConsumerId(null);
    };
  }, [consumerId, isGenerating, readConsumerId, writeConsumerId]);

  // NOTE: the `skip` option on `useQuery` also declaratively controls whether
  // polling continues, so we only need to explicitly stop polling when the
  // query responds with an error
  const skip =
    isSampleGenerated(image) ||
    !isGenerating ||
    (!!pollingConsumerId && pollingConsumerId !== consumerId) ||
    clientStatus === GeneratedImageStatus.TIMED_OUT;

  const { data, stopPolling, error, startPolling } = useAiGeneratedImageQuery({
    variables: { id },
    context: { headers: { 'x-polling-consumer-id': consumerId } },
    fetchPolicy: 'network-only',
    pollInterval: POLLING_INTERVAL,
    skip,
    onError: pollError => {
      pollError.message = `AiGeneratedImage polling error: ${pollError.message}`;
      logError(pollError);

      if (numRetries < MAX_RETRIES) {
        setNumRetries(numRetries + 1);
      } else {
        stopPolling();
      }
    },
  });

  const polledImage = data?.aiGeneratedImage;
  const isStillGenerating = isGeneratingImage(polledImage);

  useInterval(
    () => {
      if (!polledImage || !isStillGenerating) return;

      const duration = Date.now() - imageLoadStartTime;

      if (duration > maxPollingTime) {
        stopPolling();
        updateClientStatus(GeneratedImageStatus.TIMED_OUT);

        if (!isSampleGenerated(polledImage)) {
          reportFailedStatus(polledImage.aiOriginalImageId, polledImage.id);
        }
      }
    },
    isStillGenerating ? POLLING_INTERVAL : null,
  );

  useEffect(() => {
    if (!polledImage || isStillGenerating) return;

    stopPolling();

    const {
      aiOriginalImageId: originalImageId,
      id: imageId,
      pollingConsumerId: imageConsumerId,
      status,
      createdAt,
      updatedAt,
    } = polledImage;

    // Don't track generation completion events if the image was locked by
    // another consumer. Note that this shouldn't happen as we shouldn't be
    // polling in that case, but in case of a race condition we want to avoid
    // duplicate tracking calls.
    if (imageConsumerId !== consumerId) return;

    if (status === GeneratedImageStatus.FAILED) {
      reportFailedStatus(originalImageId, imageId);
    } else {
      emitPageAction(EventType.DESIGNER_GENERATED_IMAGE_RECEIVED, {
        aiOriginalImageId: `${originalImageId}`,
        aiGeneratedImageId: `${imageId}`,
      });

      const imageLoadEndTime = Date.now();

      imageLoaded({
        ...trackingData,
        ai_original_image_id: originalImageId,
        output_id: `${imageId}`,
        request_timestamp_ms: imageLoadStartTime,
        response_timestamp_ms: imageLoadEndTime,
        loading_time_ms: imageLoadEndTime - imageLoadStartTime,
        server_loading_time_ms:
          createdAt && updatedAt
            ? Date.parse(updatedAt) - Date.parse(createdAt)
            : undefined,
        space_type: spaceType || undefined,
        track_id: 'image-generated',
        went_offline: wentOffline,
      });
    }
  }, [
    consumerId,
    imageLoadStartTime,
    isStillGenerating,
    polledImage,
    spaceType,
    stopPolling,
    trackingData,
    wentOffline,
  ]);

  return {
    error,
    retry: useCallback(() => startPolling(POLLING_INTERVAL), [startPolling]),
  };
};

const sampleFromArray = (sampleSize: number, styles: string[]) => {
  const shuffled = sortBy(() => Math.random(), styles);
  return take(sampleSize, shuffled);
};

/**
 * A wrapper around the usage of the `useGenerateImageHandler` hook.
 * This returns a tuple that contains:
 *    - A function to request a batch of images by style
 *    - The generated image result
 *    - A boolean indicating whether the callback function is ready (i.e. the available styles
 *      have been fetched) or not. If the callback function is called before this boolean
 *      is true, the "all styles" case can't generate any images because it needs to know which
 *      styles to use to generate; the batch of the same styles will work regardless of this boolean value.
 *
 * This hook handles the case of being passed an "All styles" style which is a client-side concept.
 */
export const useGenerateSetOfImages = (
  originalImage: AiOrSampleOriginalImage,
) => {
  const { user } = useHomeownerAppContext();
  const styles = useAvailableStyles(
    getImageOrgId(originalImage, user),
    originalImage.spaceType,
  );

  const [generateImageHandler, generateImageResult] = useGenerateImageHandler({
    ...DEFAULT_CONFIG,
    aiOriginalImageId: Number(getImageId(originalImage)),
  });

  const generateSetOfImages = useCallback(
    (style: string, numberOfImages: number) => {
      if (style === ALL_STYLES) {
        const sampleOfStyles = sampleFromArray(numberOfImages, styles);
        sampleOfStyles.forEach(styleName => generateImageHandler(styleName));
      } else {
        for (let i = 0; i < numberOfImages; i += 1) {
          generateImageHandler(style);
        }
      }
    },
    [styles, generateImageHandler],
  );

  return [generateSetOfImages, generateImageResult, styles.length > 0] as const;
};
